import React, {useEffect, useState} from 'react';
import _ from 'underscore';
import {splitEventHandler} from '@netx/core-next/utils';
import {
	Tooltip as MuiTooltip,
	type TooltipProps as MuiTooltipProps,
} from '@mui/material';
import {useUpdateEffect} from '@netx/core-components/hooks/useUpdateEffect';

export interface TooltipProps extends MuiTooltipProps {
	disabled?: boolean;
	onTooltipToggle?: (open: boolean) => void;
}

/**
 * Wrap mui `Tooltip` to support dismissing on scroll as well as a `disabled` prop
 */
export const Tooltip = ({
	disabled,
	children,
	title,
	open: _open,
	onClose,
	onOpen,
	onTooltipToggle,
	...props
}: TooltipProps) => {
	const [open, setOpen] = useState(_open || false);

	useEffect(() => {
		if (!open) return;
		const listener = _.throttle(() => setOpen(false), 50);
		// ensure it is passive
		window.addEventListener('scroll', listener, true);
		return () => {
			window.removeEventListener('scroll', listener, true);
		};
	}, [open]);

	const toggleOpen = (open: boolean) => () => setOpen(disabled ? false : open);

	useUpdateEffect(() => {
		_open != null && setOpen(_open);
	}, [_open]);

	useUpdateEffect(() => {
		disabled && setOpen(false);
	}, [disabled]);

	return (
		<MuiTooltip
			disableInteractive
			title={disabled ? '' : title}
			{...props}
			open={open}
			onOpen={splitEventHandler(onOpen, toggleOpen(true))}
			onClose={splitEventHandler(onClose, toggleOpen(false))}
			TransitionProps={{
				...props.TransitionProps,
				onEntering: splitEventHandler(props.TransitionProps?.onEntering, () =>
					onTooltipToggle?.(true),
				),
				onExited: splitEventHandler(props.TransitionProps?.onExited, () =>
					onTooltipToggle?.(false),
				),
			}}
		>
			{children}
		</MuiTooltip>
	);
};

export default Tooltip;
